import {capitalize} from 'lodash';

export const CUSTOM_POLICY_BUILDER_FEATURE_NAME = 'Custom Policy Builder';

const RuleTypes = Object.freeze({
  GROUP: 'group',
  RULE: 'rule',
});

enum FilterTypes {
  FAMILY = 'family',
  PLATFORM = 'platform',
  PLATFORMS = 'platforms',
  PUBLISHER = 'publisher',
  STATUS = 'status',
  ASSESSMENT_STATUS = 'assessmentStatus',
  VERSION = 'version',
  AGENT_COMPATIBLE = 'agentCompatible',
}

const AgentCompatibleFilters = Object.freeze({
  FALSE: 'FALSE',
  PENDING: 'PENDING',
  TRUE: 'TRUE',
});

export const Publishers = Object.freeze({
  CIS: 'CIS',
  CONTROLS: 'CONTROLS',
  CUSTOM_AGENT: 'Custom (Agent-Based)',
  DISASTIGS: 'DISASTIGS',
  FDCC: 'FDCC',
  USGCB: 'USCGCB',
});

const FORM_NAMES = {
  addTest: 'form/ADD_TEST',
  editCpe: 'form/EDIT_CPE',
  editTest: 'form/EDIT_TEST',
};

const PolicyHandlerOperator = Object.freeze({
  AND: 'AND',
  EQUALS: 'EQUALS',
  ONE: 'ONE',
  OR: 'OR',
  XOR: 'XOR',
});

const PolicyHandlerType = Object.freeze({
  NUMBER: 'NUMBER',
});

const PolicyHandlerNegate = Object.freeze({
  FALSE: {
    BOOLEAN: false,
    NUM: 0,
    NUM_STRING: '0',
    STRING: 'false',
  },
  TRUE: {
    BOOLEAN: true,
    NUM: 1,
    NUM_STRING: '1',
    STRING: 'true',
  },
});

const TestId = Object.freeze({
  ACCESSTOKEN: 'accesstoken',
  AUDITEVENTPOLICYSUBCATEGORIES: 'auditeventpolicysubcategories',
  BGPNEIGHBOR: 'bgpneighbor',
  CMDLET: 'cmdlet',
  DPKGINFO: 'dpkginfo',
  ENTITYOBJECTRECORDTYPE: 'EntityObjectRecordType',
  ENTITYOBJECTSTRINGTYPE: 'EntityObjectStringType',
  ENVIRONMENTVARIABLE: 'environmentvariable',
  EXTERNAL: 'external',
  FAMILY: 'family',
  FILE: 'file',
  FILE_EFFECTIVE_RIGHT_53: 'fileeffectiverights53',
  FILENAME: 'filename',
  GLOBAL: 'global',
  INETLISTENINGSERVERS: 'inetlisteningservers',
  INTERFACE: 'interface',
  KEY: 'key',
  LINE: 'line',
  LINE_SELECTION: 'line_selection',
  LOCAL: 'local',
  LOCKOUTPOLICY: 'lockoutpolicy',
  NAME: 'name',
  PARTITION: 'partition',
  PASSWORD: 'password',
  PASSWORDPOLICY: 'passwordpolicy',
  PLIST511: 'plist511',
  PROCESS58: 'process58',
  REGISTRY: 'registry',
  ROUTINGPROTOCOLAUTHINTF: 'routingprotocolauthintf',
  RPMINFO: 'rpminfo',
  RUNLEVEL: 'runlevel',
  SECTION: 'section',
  SERVICE: 'service',
  SHADOW: 'shadow',
  SHELL: 'shell',
  SHELLCOMMAND: 'shellcommand',
  SID_SID: 'sid_sid',
  SMF: 'smf',
  SNMP: 'snmp',
  SNMPCOMMUNITY: 'snmpcommunity',
  SNMPGROUP: 'snmpgroup',
  SNMPHOST: 'snmphost',
  SNMPUSER: 'snmpuser',
  SQL57: 'sql57',
  SYSCTL: 'sysctl',
  SYSTEMDUNITPROPERTY: 'systemdunitproperty',
  TEXTFILECONTENT54: 'textfilecontent54',
  UNAME: 'uname',
  UNIX_FILE: 'unix_file',
  UNKNOWN: 'unknown',
  USERRIGHT: 'userright',
  USERSID: 'user_sid',
  USERSID55: 'user_sid55',
  USERSID55C: 'UserSid55',
  USERTEST: 'user',
  VARIABLE: 'variable',
  VM_ADVANCEDSETTING: 'vm_advancedsetting',
  VMHOST_ADVANCEDSETTING: 'vmhost_advancedsetting',
  VMHOST_MODULE: 'vmhost_module',
  WINDOWS_FILE: 'windows_file',
  WMI57: 'wmi57',
  XINETD: 'xinetd',
  XMLFILECONTENT: 'xmlfilecontent',
});

const SchemaCategoryStrings = Object.freeze({
  obj: {
    name: '_object',
    type: 'Object',
  },
  state: {
    name: '_state',
    type: 'State',
  },
  test: {
    name: '_test',
    type: 'Test',
  },
  variable: {
    name: '_variable',
    type: 'Variable',
  },
});

const TestFamily = Object.freeze({
  CMD: 'cmd',
  EXTENSION: 'extension',
  INDEPENDENT: 'independent',
  IOS: 'ios',
  LINUX: 'linux',
  MACOS: 'macos',
  SOLARIS: 'solaris',
  UNIX: 'unix',
  VMWARE_ESXI: 'esxi',
  WINDOWS: 'windows',
});

const FamilyTestOptions = Object.freeze({
  ANDROID: 'android',
  APPLE_IOS: 'apple_ios',
  ASA: 'asa',
  CATOS: 'catos',
  IOS: 'ios',
  IOSXE: 'iosxe',
  JUNOS: 'junos',
  MACOS: 'macos',
  PIXOS: 'pixos',
  UNDEFINED: 'undefined',
  UNIX: 'unix',
  VMWARE_INFRASTRUCTURE: 'vmware_infrastructure',
  WINDOWS: 'windows',
});

const TestCategory = Object.freeze({
  CISCO: 'cisco',
});

const getTestSection = (
  testId: string,
  category = SchemaCategoryStrings.test
) => `${testId}${category.name}`;

const buildSchemaName = (family: string, rootId: string, category: any) => {
  const editedFamily = family === '' ? family : `#${family}`;
  const id = category === '' ? rootId : `${rootId}${category.name}`;

  return `{http://oval.mitre.org/XMLSchema/oval-definitions-5${editedFamily}}${id}`;
};

const buildSchemaType = (family: string, rootId: string, category: any) => {
  const editedFamily = family === '' ? family : `.${family}`;
  const id = category === '' ? rootId : `${capitalize(rootId)}${category.type}`;

  return `com.rapid7.model.scap.jaxb.oval511${editedFamily}.${id}`;
};

const updateStrucNameType = (struct: Record<string, any>, category: string) => {
  const nodeTypeMap = {
    obj: SchemaCategoryStrings.obj,
    ste: SchemaCategoryStrings.state,
    tst: SchemaCategoryStrings.test,
  };

  Object.keys(nodeTypeMap).forEach((type) => {
    if (!(type in struct)) {
      struct[type] = {};
    }
    struct[type].name = buildSchemaName(
      struct.family,
      struct.baseType,
      nodeTypeMap[type as keyof typeof nodeTypeMap]
    );
    const family = category ? `${category}.${struct.family}` : struct.family;

    struct[type].type = buildSchemaType(
      family,
      struct.baseType,
      nodeTypeMap[type as keyof typeof nodeTypeMap]
    );
  });
};

const PolicyDocumentConstants = Object.freeze({
  VARIABLE_NAME_TYPE: buildSchemaName(
    '',
    TestId.EXTERNAL,
    SchemaCategoryStrings.variable
  ),
});

const PolicyTestDatatype = Object.freeze({
  BEHAVIORS: 'behaviors',
  BINARY: 'binary',
  BOOLEAN: 'boolean',
  EVR_STRING: 'evr_string',
  INT: 'int',
  RECORD: 'record',
  STRING: 'string',
  VERSION: 'version',
});

const PolicyComponentType = Object.freeze({
  INPUT: 'input',
  MULTI_FIELD: 'multiField',
  SELECT: 'select',
  TEXT_AREA: 'textArea',
});

const PolicyTestBehaviors = Object.freeze({
  '32_BIT': '32_bit',
  '64_BIT': '64_bit',
  ALL: 'all',
  DEFINED: 'defined',
  DIRECTORIES: 'directories',
  DOWN: 'down',
  FILES: 'files',
  FILES_DIRECTORIES: 'files and directories',
  LOCAL: 'local',
  NONE: 'none',
  SYMLINKS: 'symlinks',
  SYMLINKS_DIRECTORIES: 'symlinks and directories',
  UP: 'up',
});

const PolicyTestBehaviorGroups = Object.freeze({
  RECURSE: [
    PolicyTestBehaviors.NONE,
    PolicyTestBehaviors.FILES,
    PolicyTestBehaviors.DIRECTORIES,
    PolicyTestBehaviors.FILES_DIRECTORIES,
    PolicyTestBehaviors.SYMLINKS,
    PolicyTestBehaviors.SYMLINKS_DIRECTORIES,
  ],
  RECURSE_DIRECTION: [
    PolicyTestBehaviors.NONE,
    PolicyTestBehaviors.UP,
    PolicyTestBehaviors.DOWN,
  ],
  RECURSE_FILE_SYSTEM: [
    PolicyTestBehaviors.ALL,
    PolicyTestBehaviors.LOCAL,
    PolicyTestBehaviors.DEFINED,
  ],
  WINDOWS_VIEW: [PolicyTestBehaviors['32_BIT'], PolicyTestBehaviors['64_BIT']],
});

const PolicyTestOperation = Object.freeze({
  BITWISE_AND: 'bitwise and',
  BITWISE_OR: 'bitwise or',
  CASE_INSENSITIVE_EQUALS: 'case insensitive equals',
  CASE_INSENSITIVE_NOT_EQUAL: 'case insensitive not equal',
  EQUALS: 'equals',
  GREATER_THAN: 'greater than',
  GREATER_THAN_OR_EQUAL: 'greater than or equal',
  LESS_THAN: 'less than',
  LESS_THAN_OR_EQUAL: 'less than or equal',
  NOT_EQUAL: 'not equal',
  PATTERN_MATCH: 'pattern match',
  SUBSET_OF: 'subset of',
  SUPERSET_OF: 'superset of',
});

const PolicyTestFeatures = Object.freeze({
  EMPTY_STRING_EQUIV_NULL: 3,
  SHOW_ENTITY_CHECK: 1,
});

const PolicyOperationsByType = Object.freeze({
  BOOLEAN: [PolicyTestOperation.EQUALS, PolicyTestOperation.NOT_EQUAL],
  EVR_STRING: [
    PolicyTestOperation.EQUALS,
    PolicyTestOperation.NOT_EQUAL,
    PolicyTestOperation.LESS_THAN,
    PolicyTestOperation.LESS_THAN_OR_EQUAL,
    PolicyTestOperation.GREATER_THAN,
    PolicyTestOperation.GREATER_THAN_OR_EQUAL,
  ],
  INT: [
    PolicyTestOperation.EQUALS,
    PolicyTestOperation.NOT_EQUAL,
    PolicyTestOperation.LESS_THAN,
    PolicyTestOperation.LESS_THAN_OR_EQUAL,
    PolicyTestOperation.GREATER_THAN,
    PolicyTestOperation.GREATER_THAN_OR_EQUAL,
    PolicyTestOperation.BITWISE_AND,
    PolicyTestOperation.BITWISE_OR,
  ],
  STRING: [
    PolicyTestOperation.EQUALS,
    PolicyTestOperation.NOT_EQUAL,
    PolicyTestOperation.CASE_INSENSITIVE_EQUALS,
    PolicyTestOperation.CASE_INSENSITIVE_NOT_EQUAL,
    PolicyTestOperation.PATTERN_MATCH,
  ],
  VERSION: [
    PolicyTestOperation.EQUALS,
    PolicyTestOperation.NOT_EQUAL,
    PolicyTestOperation.LESS_THAN,
    PolicyTestOperation.LESS_THAN_OR_EQUAL,
    PolicyTestOperation.GREATER_THAN,
    PolicyTestOperation.GREATER_THAN_OR_EQUAL,
  ],
});

const UncategorizedRulesAttributes = Object.freeze({
  id: 'policyRules',
  title: 'Policy Rules',
});

const SearchState = Object.freeze({
  ID: '_searchResults',
  TITLE: '_searchResults',
});

const CommonPolicyTestSections = Object.freeze({
  ADD_NEW: 'addNew',
  ADD_NEW_OPTION: 'addNewOption',
  ATTRS: 'attrs',
  BEHAVIORS: 'behaviors',
  FILTER: 'filter',
  INSTANCE: 'instance',
  OBJECT: 'object',
  STATE: 'state',
});

const PolicyTestCheckExistence = Object.freeze({
  ALL_EXIST: 'all_exist',
  ANY_EXIST: 'any_exist',
  AT_LEAST_ONE_EXISTS: 'at_least_one_exists',
  NONE_EXIST: 'none_exist',
  ONLY_ONE_EXISTS: 'only_one_exists',
});

const PolicyTestCheck = Object.freeze({
  ALL: 'all',
  AT_LEAST_ONE: 'at least one',
  NONE_EXIST: 'none exist',
  NONE_SATISFY: 'none satisfy',
  ONLY_ONE: 'only one',
});

// list of affected policies. (some rules aren't assessed)
const AFFECTED_POLICIES = [
  'xccdf_org.cisecurity.benchmarks_benchmark_3.1.2_CIS_CentOS_Linux_7_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.3.0_CIS_Apache_HTTP_Server_2.4_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0.1_CIS_Mozilla_Firefox_24_ESR_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_2.0.0_CIS_Amazon_Linux_2_STIG_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_2.0.0_CIS_Amazon_Linux_2_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_2.0.0_CIS_Ubuntu_Linux_16.04_LTS_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_2.0.0_CIS_Oracle_Linux_8_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.1_CIS_CentOS_Linux_8_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.1_CIS_Red_Hat_Enterprise_Linux_8_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.1_CIS_Debian_Linux_9_Benchmark',
  'xccdf_mil.disa.stig_benchmark_MOZ_Firefox_RHEL_V5R2_STIG',
  'xccdf_mil.disa.stig_benchmark_Oracle_Linux_7_V2R3_STIG',
  'xccdf_mil.disa.stig_benchmark_Apache_Server_2-4_UNIX_Server_STIG_V2R3',
  'xccdf_mil.disa.stig_benchmark_CAN_Ubuntu_16-04_LTS_V2R3_STIG',
  'xccdf_mil.disa.stig_benchmark_Windows_Server_2016_V2R2_STIG',
  'xccdf_mil.disa.stig_benchmark_Windows_10_V2R2_STIG',
  'xccdf_mil.disa.stig_benchmark_CAN_Ubuntu_18-04_V2R7_STIG',
  'xccdf_mil.disa.stig_benchmark_Windows_Server_2019_V2R2_STIG',
  'xccdf_mil.disa.stig_benchmark_Apache_Server_2-4_UNIX_Site_STIG_V2R2',
  'xccdf_org.cisecurity.benchmarks_benchmark_3.0.0_CIS_SUSE_Linux_Enterprise_12_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_MongoDB_3.2_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_AlmaLinux_OS_8_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_SUSE_Linux_Enterprise_15_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_Mozilla_Firefox_38_ESR_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_MIT_Kerberos_1.10_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_Ubuntu_Linux_20.04_LTS_STIG_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_Fedora_19_Family_Linux_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_Microsoft_Internet_Explorer_11_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.0.0_CIS_Debian_Linux_10_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_2.0.1_CIS_Fedora_28_Family_Linux_Benchmark',
  'xccdf_mil.disa.stig_benchmark_Windows_2012_MS_V3R1_STIG',
  'xccdf_mil.disa.stig_benchmark_RHEL_7_V3R4_STIG',
  'xccdf_mil.disa.stig_benchmark_Windows_2012_DC_V3R3_STIG',
  'xccdf_org.cisecurity.benchmarks_benchmark_2.1.0_CIS_Ubuntu_Linux_14.04_LTS_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_2.1.0_CIS_Ubuntu_Linux_18.04_LTS_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.1.0_CIS_Ubuntu_Linux_20.04_LTS_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.1.0_CIS_MongoDB_3.6_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_3.1.1_CIS_Red_Hat_Enterprise_Linux_7_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_3.1.1_CIS_Oracle_Linux_7_Benchmark',
  'xccdf_mil.disa.stig_benchmark_Canonical_Ubuntu_20-04_LTS_V1R3_STIG',
  'xccdf_mil.disa.stig_benchmark_McAfee_VirusScan88_Managed_Client',
  'xccdf_mil.disa.stig_benchmark_McAfee_VirusScan88_Local_Client_V1R4_STIG',
  'xccdf_mil.disa.stig_benchmark_MOZ_Firefox_Linux_V6R2_STIG',
  'xccdf_org.cisecurity.benchmarks_benchmark_1.6.1_CIS_Kubernetes_Benchmark',
  'xccdf_org.cisecurity.benchmarks_benchmark_2.0.2_CIS_Debian_Linux_8_Benchmark',
];

export {
  RuleTypes,
  PolicyDocumentConstants,
  PolicyTestCheck,
  PolicyTestDatatype,
  PolicyComponentType,
  CommonPolicyTestSections,
  PolicyOperationsByType,
  PolicyTestFeatures,
  PolicyTestOperation,
  FORM_NAMES,
  FilterTypes,
  AgentCompatibleFilters,
  PolicyHandlerOperator,
  PolicyHandlerNegate,
  PolicyHandlerType,
  SchemaCategoryStrings,
  TestCategory,
  TestFamily,
  FamilyTestOptions,
  TestId,
  buildSchemaName,
  buildSchemaType,
  UncategorizedRulesAttributes,
  getTestSection,
  PolicyTestCheckExistence,
  SearchState,
  updateStrucNameType,
  PolicyTestBehaviors,
  PolicyTestBehaviorGroups,
  AFFECTED_POLICIES,
};
